import { yupResolver } from '@hookform/resolvers/yup';
import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import AlertModal from 'components/common/AlertModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import ImageUploadControl from 'components/formfields/ImageUploadControl';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { useEffect, useState } from 'react';
import { Button, Form, Modal, Stack, Table } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { displayError, handleServiceError, IMAGE_SIZES } from 'utils';
import * as yup from 'yup';
import characterSettingService from '../../characterSettingService';
import { CharacterSetting } from '../../models/characterSetting';
import './CharacterSettingDetailModal.scss';

interface Props {
  show: boolean;
  onClose: () => void;
  character: CharacterSetting | null;
  onSave: (character: any) => void;
}

export default function CharacterSettingDetailModal({ show, onClose, character, onSave }: Props) {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();

  // State
  const [isLoading, setIsLoading] = useState(false);
  const [formKey, setFormKey] = useState(0);

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);
  // Form validation schema
  const schema = yup.object({
    name: yup.string().required(t('characterSettingDetail.name_required')),
    idle_time: yup
      .number()
      .transform((value, originalValue) => {
        if (originalValue === '' || originalValue == null) return undefined;
        if (originalValue === '-' || Number.isNaN(value)) return 0;
        return value;
      })
      .integer('整数を入力してください')
      .min(1, '値は 1 以上である必要があります。')
      .required(t('characterSettingDetail.idle_time_required')),
    image_1: yup.string().required(t('characterSettingDetail.image_1_required')).nullable(),
    image_2: yup.string().required(t('characterSettingDetail.image_2_required')).nullable(),
    status: yup.boolean(),
  });

  // React Hook Form setup
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<Partial<CharacterSetting>>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      idle_time: 10,
      image_1: '',
      image_2: '',
      status: false,
      delete_flg: false,
    },
    mode: 'onSubmit',
    shouldUnregister: false,
  });

  useEffect(() => {
    if (!character) {
      setFormKey((prev) => prev + 1);
      reset({
        name: '',
        idle_time: 10,
        image_1: '',
        image_2: '',
        status: false,
        delete_flg: false,
      });
    } else {
      // Editing existing character
      reset({
        name: character.name,
        idle_time: character.idle_time || 10,
        image_1: character.image_1 || '',
        image_2: character.image_2 || '',
        status: character.status || false,
        delete_flg: character.delete_flg || false,
      });
    }
  }, [character, reset]);

  const resetFormToEmpty = () => {
    setFormKey((prev) => prev + 1);
    reset({
      name: '',
      idle_time: 10,
      image_1: '',
      image_2: '',
      status: false,
      delete_flg: false,
    });
  };

  const handleSave = async (data: Partial<CharacterSetting>) => {
    setIsLoading(true);

    try {
      if (character?.id) {
        // Update existing character
        const updatePayload = {
          id: character.id,
          name: data.name?.trim() || '',
          image_1: data.image_1 || null,
          image_2: data.image_2 || null,
          idle_time: data.idle_time || 10,
          status: data.status || false,
        };

        const response = await characterSettingService.update(character.id, updatePayload);
        if (response?.data) {
          showGlobalModal(() => (
            <AlertModal title={t('App.info')} message={t('characterSettingDetail.save_success')} />
          ));
          onSave(response.data);
        }
      } else {
        // Create new character
        const createPayload = {
          name: data.name?.trim() || '',
          idle_time: data.idle_time || 10,
          image_1: data.image_1 || null,
          image_2: data.image_2 || null,
        };

        const response = await characterSettingService.create(createPayload);
        if (response?.data) {
          resetFormToEmpty();
          showGlobalModal(() => (
            <AlertModal title={t('App.info')} message={t('characterSettingDetail.create_success')} />
          ));
          onSave(response.data);
        }
      }
    } catch (error) {
      if (!handleServiceError(showGlobalModal, error as AxiosError)) {
        displayError(showGlobalModal, error as AxiosError);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} dialogClassName="characterSetting-detail-modal" backdrop="static" centered>
      <Modal.Header closeButton className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <FeatureTitleBar title={t('characterSettingDetail.detail_title')} />
      </Modal.Header>

      <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <form
          key={`${formKey}-${character ? `edit-${character.id}` : 'create'}`}
          onSubmit={handleSubmit(handleSave)}
          noValidate
        >
          <div className="d-flex justify-content-end mb-3">
            <div>
              <Button type="submit" className="btn btn--dark-green" disabled={isLoading}>
                {isLoading ? t('common.loading') : t('common.save')}
              </Button>
            </div>
          </div>

          <Table bordered className="form-character">
            <tbody>
              {/* Character Name */}
              <tr className="row-height-50">
                <th className="text-center table-header" colSpan={2}>
                  {t('characterSettingDetail.character_name')}
                </th>
                <td className="table-cell width-50" colSpan={2}>
                  <Form.Control className="text-center" {...register('name')} isInvalid={!!errors.name} />
                  {errors.name && <small className="text-danger d-inline-block mt-1">{errors.name.message}</small>}
                </td>
              </tr>

              {/* Character Images */}
              <tr className={`${control._formValues.image_1 !== '' ? 'row-height-100' : ''} text-center`}>
                <th className="table-header width-20" rowSpan={2} colSpan={1}>
                  {t('characterSettingDetail.character_image')}
                </th>
                {/* Image 1 */}
                <th className="table-header width-20 text-center" colSpan={1}>
                  {t('characterSettingDetail.image_1')}
                </th>
                <td colSpan={1} style={{ verticalAlign: 'bottom' }}>
                  <Stack style={{ position: 'relative' }}>
                    {errors.image_1 && (
                      <small
                        className="text-danger d-inline-block mt-2"
                        style={{
                          position: 'absolute',
                          zIndex: 10,
                        }}
                      >
                        {errors.image_1.message}
                      </small>
                    )}
                    <ImageUploadControl
                      name="image_1"
                      control={control}
                      minWidth={IMAGE_SIZES.character_image_size.minWidth}
                      minHeight={IMAGE_SIZES.character_image_size.minHeight}
                      maxWidth={IMAGE_SIZES.character_image_size.maxWidth}
                      maxHeight={IMAGE_SIZES.character_image_size.maxHeight}
                      maxFileSize={IMAGE_SIZES.character_image_size.maxFileSize}
                      style={{ maxHeight: '120px' }}
                    />
                  </Stack>
                </td>
              </tr>
              {/* Image 2 */}
              <tr className={`${control._formValues.image_2 !== '' ? 'row-height-100' : ''} text-center`}>
                <th className="table-header width-20" colSpan={1}>
                  {t('characterSettingDetail.image_2')}
                </th>
                <td colSpan={1} style={{ verticalAlign: 'bottom' }}>
                  <Stack style={{ position: 'relative' }}>
                    {errors.image_2 && (
                      <small
                        className="text-danger d-inline-block mt-2"
                        style={{
                          position: 'absolute',
                          zIndex: 10,
                        }}
                      >
                        {errors.image_2.message}
                      </small>
                    )}
                    <ImageUploadControl
                      name="image_2"
                      control={control}
                      minWidth={IMAGE_SIZES.character_image_size.minWidth}
                      minHeight={IMAGE_SIZES.character_image_size.minHeight}
                      maxWidth={IMAGE_SIZES.character_image_size.maxWidth}
                      maxHeight={IMAGE_SIZES.character_image_size.maxHeight}
                      maxFileSize={IMAGE_SIZES.character_image_size.maxFileSize}
                      style={{ maxHeight: '120px' }}
                    />
                  </Stack>
                </td>
              </tr>

              {/* Idle Time */}
              <tr className="row-height-50">
                <th className="text-center table-header width-40" colSpan={2}>
                  {t('characterSettingDetail.idle_time')}
                </th>
                <td className="table-cell width-60" colSpan={2}>
                  <Form.Control
                    type="number"
                    className="text-center"
                    {...register('idle_time', { valueAsNumber: true })}
                    isInvalid={!!errors.idle_time}
                  />
                  {errors.idle_time && (
                    <small className="text-danger d-inline-block mt-1">{errors.idle_time.message}</small>
                  )}
                </td>
              </tr>
            </tbody>
          </Table>
        </form>
      </Modal.Body>
    </Modal>
  );
}
